import React, { useState, useCallback, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Upload, Database, RefreshCw, CheckCircle2, XCircle,
  AlertTriangle, FileSpreadsheet, Loader2, Check, Clock,
  Trash2, FileUp, Zap, ArrowRight, GitMerge, History,
  Plus, Pencil, Minus, ChevronDown, ChevronRight, Eye,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DatabaseSync = () => {
  const [uploadStatus, setUploadStatus] = useState(null);
  const [adjStatus, setAdjStatus] = useState(null);

  // Task merge
  const [uploadingTasks, setUploadingTasks] = useState(false);
  const [taskPreview, setTaskPreview] = useState(null);
  const [committingTasks, setCommittingTasks] = useState(false);

  // Duration merge
  const [uploadingDuration, setUploadingDuration] = useState(false);
  const [durationPreview, setDurationPreview] = useState(null);
  const [committingDuration, setCommittingDuration] = useState(false);
  const [durationMode, setDurationMode] = useState("merge"); // "merge" | "add_only"
  const [skippedChangeKeys, setSkippedChangeKeys] = useState(new Set()); // "<adj_id>:<field>"
  const [showCommitConfirm, setShowCommitConfirm] = useState(false);

  // Change log
  const [showChangelog, setShowChangelog] = useState(false);
  const [changelog, setChangelog] = useState([]);
  const [changelogDetail, setChangelogDetail] = useState(null);

  // Drag & drop
  const [dragActiveTask, setDragActiveTask] = useState(false);
  const [dragActiveDuration, setDragActiveDuration] = useState(false);

  // Expanded sections in preview
  const [expandedSections, setExpandedSections] = useState({ new: true, changed: true, removed: false });

  // Legacy sync/copy
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [showAdjCopyPrompt, setShowAdjCopyPrompt] = useState(false);
  const [adjCopying, setAdjCopying] = useState(false);

  const fetchStatus = async () => {
    try {
      const [upRes, adjRes] = await Promise.all([
        axios.get(`${API}/upload-status`),
        axios.get(`${API}/adjustments/status`),
      ]);
      setUploadStatus(upRes.data);
      setAdjStatus(adjRes.data);
    } catch {}
  };

  useEffect(() => { fetchStatus(); }, []);

  const toggleSection = (s) => setExpandedSections(prev => ({ ...prev, [s]: !prev[s] }));

  // ═══════════════════════════════════════════
  // CHUNKED UPLOAD HELPER
  // ═══════════════════════════════════════════
  const chunkedUpload = async (file, endpoint) => {
    const CHUNK_SIZE = 512 * 1024; // 512KB
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

    if (totalChunks <= 1) {
      // Small file — direct upload
      const fd = new FormData();
      fd.append("file", file);
      return await axios.post(`${API}/${endpoint}`, fd, { timeout: 180000 });
    }

    // Large file — chunked upload via merge chunk endpoints
    const uploadId = `merge_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const chunk = file.slice(start, start + CHUNK_SIZE);
      const fd = new FormData();
      fd.append("chunk", chunk);
      fd.append("upload_id", uploadId);
      fd.append("chunk_index", String(i));
      fd.append("total_chunks", String(totalChunks));
      fd.append("filename", file.name);
      await axios.post(`${API}/merge/upload-chunk`, fd, { timeout: 60000 });
    }
    // Finalize — triggers the actual merge preview
    return await axios.post(`${API}/merge/finalize-upload`, {
      upload_id: uploadId,
      merge_type: endpoint.includes("duration") ? "duration" : "task",
      filename: file.name,
    }, { timeout: 180000 });
  };

  // ═══════════════════════════════════════════
  // TASK MERGE UPLOAD (supports multiple files)
  // ═══════════════════════════════════════════
  const handleTaskUpload = async (files) => {
    const fileList = Array.isArray(files) ? files : [files];
    const validFiles = fileList.filter(f => f && (f.name.endsWith('.xlsx') || f.name.endsWith('.xls')));
    if (validFiles.length === 0) { toast.error("Please upload Excel files (.xlsx or .xls)"); return; }

    setUploadingTasks(true);
    setTaskPreview(null);

    try {
      let combinedPreview = null;

      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i];
        toast.info(`Processing file ${i + 1}/${validFiles.length}: ${file.name}`);

        // If there's a pending preview from previous file, commit it first (skip removed)
        if (combinedPreview?.preview_id) {
          await axios.post(`${API}/merge/commit-tasks`, { preview_id: combinedPreview.preview_id, handle_removed: "skip" });
        }

        const res = await chunkedUpload(file, "merge/upload-tasks");
        combinedPreview = res.data;
      }

      if (combinedPreview) {
        setTaskPreview(combinedPreview);
        toast.success(`${validFiles.length} file(s) processed: ${combinedPreview.new_count} new, ${combinedPreview.changed_count} changed`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to process task sheet(s)");
    } finally {
      setUploadingTasks(false);
    }
  };

  const commitTaskMerge = async (handleRemoved = "skip") => {
    if (!taskPreview?.preview_id) return;
    setCommittingTasks(true);
    try {
      const res = await axios.post(`${API}/merge/commit-tasks`, {
        preview_id: taskPreview.preview_id,
        handle_removed: handleRemoved,
      });
      toast.success(`Merge committed: ${res.data.inserted} added, ${res.data.updated} updated${res.data.duration_synced > 0 ? `, ${res.data.duration_synced} durations synced` : ""}`);
      setTaskPreview(null);
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to commit merge");
    } finally {
      setCommittingTasks(false);
    }
  };

  const cancelTaskPreview = async () => {
    if (taskPreview?.preview_id) {
      await axios.delete(`${API}/merge/preview/${taskPreview.preview_id}`).catch(() => {});
    }
    setTaskPreview(null);
  };

  // ═══════════════════════════════════════════
  // DURATION MERGE UPLOAD
  // ═══════════════════════════════════════════
  const handleDurationUpload = async (file) => {
    if (!file || (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls'))) {
      toast.error("Please upload an Excel file (.xlsx or .xls)");
      return;
    }
    setUploadingDuration(true);
    setDurationPreview(null);
    setDurationMode("merge");
    setSkippedChangeKeys(new Set());
    try {
      const res = await chunkedUpload(file, "merge/upload-duration");
      setDurationPreview(res.data);
      const newLbl = res.data.duration_only_new_count ? `, ${res.data.duration_only_new_count} new duration-only tasks` : "";
      toast.success(`Duration preview: ${res.data.matched_count} matched, ${res.data.changes_count} changes${newLbl}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to process duration sheet");
    } finally {
      setUploadingDuration(false);
    }
  };

  const commitDurationMerge = async () => {
    if (!durationPreview?.preview_id) return;
    setCommittingDuration(true);
    try {
      const res = await axios.post(`${API}/merge/commit-duration`, {
        preview_id: durationPreview.preview_id,
        mode: durationMode,
        skip_fields: Array.from(skippedChangeKeys),
      });
      const { verified, synced, skipped_rows, skipped_fields, mode, duration_only_inserted } = res.data;
      const newLbl = duration_only_inserted ? `, ${duration_only_inserted} new tasks created` : "";
      toast.success(
        mode === "add_only"
          ? `Verified ${verified} rows (Add Only — existing values kept)${newLbl}`
          : `Synced ${verified} verified, ${synced} fields updated${skipped_fields ? `, ${skipped_fields} skipped` : ""}${skipped_rows ? `, ${skipped_rows} rows kept` : ""}${newLbl}`
      );
      setDurationPreview(null);
      setSkippedChangeKeys(new Set());
      setShowCommitConfirm(false);
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to commit duration merge");
    } finally {
      setCommittingDuration(false);
    }
  };

  const toggleSkipChange = (adjId, field) => {
    const key = `${adjId}:${field}`;
    setSkippedChangeKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const cancelDurationPreview = async () => {
    if (durationPreview?.preview_id) {
      await axios.delete(`${API}/merge/preview/${durationPreview.preview_id}`).catch(() => {});
    }
    setDurationPreview(null);
  };

  // ═══════════════════════════════════════════
  // LEGACY SYNC + ADJUSTMENT COPY
  // ═══════════════════════════════════════════
  const handleLegacySync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/sync-duration`);
      setSyncResult(res.data);
      toast.success(`Synced ${res.data.synced_count} records`);
      setShowAdjCopyPrompt(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const handleCreateAdjCopy = async () => {
    setAdjCopying(true);
    try {
      const res = await axios.post(`${API}/adjustments/create-copy`);
      toast.success(`${res.data.new_copied} new tasks copied (${res.data.total_adjustments} total)`);
      setShowAdjCopyPrompt(false);
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setAdjCopying(false);
    }
  };

  // ═══════════════════════════════════════════
  // CHANGELOG
  // ═══════════════════════════════════════════
  const loadChangelog = async () => {
    try {
      const res = await axios.get(`${API}/merge/changelog?limit=20`);
      setChangelog(res.data.logs || []);
      setShowChangelog(true);
    } catch {}
  };

  const loadChangelogDetail = async (logId) => {
    try {
      const res = await axios.get(`${API}/merge/changelog/${logId}`);
      setChangelogDetail(res.data);
    } catch {}
  };

  // Drag handlers
  const [syncingPO, setSyncingPO] = useState(false);
  const [syncingTasks, setSyncingTasks] = useState(false);

  const handleSyncPOStatus = async () => {
    setSyncingPO(true);
    try {
      const res = await axios.post(`${API}/merge/sync-po-status`);
      toast.success(`PO Status synced: ${res.data.updated} tasks updated`);
      fetchStatus();
    } catch (err) { toast.error(err.response?.data?.detail || "Sync failed"); }
    finally { setSyncingPO(false); }
  };

  const handleSyncTasks = async () => {
    if (!window.confirm("Sync all task data from raw uploads to the adjustment database?\n\nThis updates: status, resource, due date, title, episode\nPreserves: rates, amounts, billing")) return;
    setSyncingTasks(true);
    try {
      const res = await axios.post(`${API}/merge/sync-tasks-to-adjustments`, {}, { timeout: 180000 });
      toast.success(`Task sync: ${res.data.updated} updated, ${res.data.new_added} new, ${res.data.duration_synced} durations synced`);
      fetchStatus();
    } catch (err) { toast.error(err.response?.data?.detail || "Sync failed"); }
    finally { setSyncingTasks(false); }
  };

  const handleDragTask = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActiveTask(e.type === "dragenter" || e.type === "dragover");
  }, []);
  const handleDropTask = useCallback((e) => {
    e.preventDefault(); e.stopPropagation(); setDragActiveTask(false);
    if (e.dataTransfer.files?.length > 0) handleTaskUpload(Array.from(e.dataTransfer.files));
  }, []);
  const handleDragDuration = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActiveDuration(e.type === "dragenter" || e.type === "dragover");
  }, []);
  const handleDropDuration = useCallback((e) => {
    e.preventDefault(); e.stopPropagation(); setDragActiveDuration(false);
    if (e.dataTransfer.files?.[0]) handleDurationUpload(e.dataTransfer.files[0]);
  }, []);

  const SectionHeader = ({ icon: Icon, title, count, color, section }) => (
    <div className="flex items-center gap-2 py-2 px-3 cursor-pointer hover:bg-slate-50 rounded-lg transition-colors"
      onClick={() => toggleSection(section)} data-testid={`section-${section}`}>
      {expandedSections[section] ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      <Icon className={`w-4 h-4 ${color}`} />
      <span className="text-sm font-semibold text-slate-700">{title}</span>
      <Badge className={`text-[10px] ${color.includes("emerald") ? "bg-emerald-100 text-emerald-700" : color.includes("amber") ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>{count}</Badge>
    </div>
  );

  return (
    <div className="p-6 max-w-full" data-testid="database-sync-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, system-ui' }}>Database Sync</h1>
          <p className="text-sm text-slate-500 mt-1">Incremental merge engine — upload, preview, commit</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleSyncPOStatus} disabled={syncingPO} className="text-teal-600 border-teal-200 hover:bg-teal-50" data-testid="sync-po-status-btn">
            {syncingPO ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Zap className="w-4 h-4 mr-1" />}
            {syncingPO ? "Syncing..." : "Sync PO Status"}
          </Button>
          <Button variant="outline" size="sm" onClick={handleSyncTasks} disabled={syncingTasks} className="text-blue-600 border-blue-200 hover:bg-blue-50" data-testid="sync-tasks-btn">
            {syncingTasks ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Database className="w-4 h-4 mr-1" />}
            {syncingTasks ? "Syncing..." : "Sync Tasks to DB"}
          </Button>
          <Button variant="outline" size="sm" onClick={loadChangelog} data-testid="changelog-btn">
            <History className="w-4 h-4 mr-1" />Change Log
          </Button>
          <Button variant="outline" size="sm" onClick={fetchStatus}>
            <RefreshCw className="w-4 h-4 mr-1" />Refresh
          </Button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"><FileSpreadsheet className="w-5 h-5 text-blue-600" /></div>
            <div><p className="text-2xl font-bold text-slate-900">{uploadStatus?.tasks_count || 0}</p><p className="text-xs text-slate-500">Raw Tasks</p></div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center"><Database className="w-5 h-5 text-indigo-600" /></div>
            <div><p className="text-2xl font-bold text-slate-900">{adjStatus?.adjustment_count || 0}</p><p className="text-xs text-slate-500">Adjustment Records</p></div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center"><FileSpreadsheet className="w-5 h-5 text-violet-600" /></div>
            <div><p className="text-2xl font-bold text-slate-900">{uploadStatus?.durations_count || 0}</p><p className="text-xs text-slate-500">Duration Records</p></div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center"><GitMerge className="w-5 h-5 text-emerald-600" /></div>
            <div><p className="text-2xl font-bold text-slate-900">{adjStatus?.archive_count || 0}</p><p className="text-xs text-slate-500">Merge History</p></div>
          </div>
        </div>
      </div>

      {/* Upload Zones */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Task Sheet Upload */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <FileUp className="w-4 h-4 text-blue-600" />Task Sheet (Original Database)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className={`border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer ${
                dragActiveTask ? 'border-blue-400 bg-blue-50' : 'border-slate-200 hover:border-blue-300 hover:bg-blue-50/30'
              }`}
              onDragEnter={handleDragTask} onDragLeave={handleDragTask} onDragOver={handleDragTask} onDrop={handleDropTask}
              onClick={() => document.getElementById('task-file-input').click()}
              data-testid="upload-task-zone"
            >
              <input type="file" id="task-file-input" accept=".xlsx,.xls" multiple
                onChange={(e) => e.target.files?.length > 0 && handleTaskUpload(Array.from(e.target.files))} className="hidden" />
              {uploadingTasks ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                  <p className="text-sm text-blue-600 font-medium">Analyzing file & generating preview...</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload className="w-8 h-8 text-slate-400" />
                  <p className="text-sm font-medium text-slate-700">Drop Task Sheet(s) here or click to select</p>
                  <p className="text-xs text-slate-400">Supports multiple files — PO Number, Atlas Job ID, Task, Language, Status, Resource</p>
                </div>
              )}
            </div>
            {!adjStatus?.has_adjustments && (
              <div className="mt-3 flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleLegacySync} disabled={syncing || !uploadStatus?.ready_for_sync} data-testid="legacy-sync-btn">
                  <Zap className="w-3.5 h-3.5 mr-1" />{syncing ? "Syncing..." : "Legacy Sync + Copy"}
                </Button>
                <span className="text-[10px] text-slate-400">First-time setup: sync duration then create adjustment copy</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Duration Sheet Upload */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <FileUp className="w-4 h-4 text-violet-600" />Duration Sheet (PO Status)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className={`border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer ${
                dragActiveDuration ? 'border-violet-400 bg-violet-50' : 'border-slate-200 hover:border-violet-300 hover:bg-violet-50/30'
              }`}
              onDragEnter={handleDragDuration} onDragLeave={handleDragDuration} onDragOver={handleDragDuration} onDrop={handleDropDuration}
              onClick={() => document.getElementById('duration-file-input').click()}
              data-testid="upload-duration-zone"
            >
              <input type="file" id="duration-file-input" accept=".xlsx,.xls"
                onChange={(e) => e.target.files?.[0] && handleDurationUpload(e.target.files[0])} className="hidden" />
              {uploadingDuration ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
                  <p className="text-sm text-violet-600 font-medium">Analyzing duration data...</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload className="w-8 h-8 text-slate-400" />
                  <p className="text-sm font-medium text-slate-700">Drop Duration Sheet here or click to select</p>
                  <p className="text-xs text-slate-400">PO Status tracking: Issued / Completed / Approved</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ═══ TASK MERGE PREVIEW ═══ */}
      {taskPreview && (
        <Card className="mb-6 border-blue-200" data-testid="task-merge-preview">
          <CardHeader className="pb-3 bg-blue-50/50">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <GitMerge className="w-4 h-4 text-blue-600" />Task Merge Preview
                <Badge className="bg-blue-100 text-blue-700 text-[10px]">{taskPreview.filename}</Badge>
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={() => commitTaskMerge("mark")} disabled={committingTasks} className="bg-blue-600 hover:bg-blue-700" data-testid="commit-merge-btn">
                  {committingTasks ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
                  {committingTasks ? "Committing..." : "Commit Merge"}
                </Button>
                <Button size="sm" variant="outline" onClick={cancelTaskPreview} data-testid="cancel-merge-btn">Cancel</Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            {/* Summary KPIs */}
            <div className="grid grid-cols-5 gap-3 mb-4">
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-slate-800">{taskPreview.uploaded_rows}</p>
                <p className="text-[10px] text-slate-500">Uploaded Rows</p>
              </div>
              <div className="bg-emerald-50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-emerald-700">{taskPreview.new_count}</p>
                <p className="text-[10px] text-emerald-600">New (Insert)</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-amber-700">{taskPreview.changed_count}</p>
                <p className="text-[10px] text-amber-600">Changed (Update)</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-slate-600">{taskPreview.unchanged_count}</p>
                <p className="text-[10px] text-slate-500">Unchanged (Skip)</p>
              </div>
              <div className="bg-red-50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-red-700">{taskPreview.removed_count}</p>
                <p className="text-[10px] text-red-600">Removed</p>
              </div>
              {taskPreview.duration_confirmed > 0 && (
                <div className="bg-teal-50 rounded-lg p-3 text-center col-span-5">
                  <p className="text-sm font-bold text-teal-700">{taskPreview.duration_confirmed} tasks confirmed completed by duration sheet (not marked as removed)</p>
                </div>
              )}
            </div>

            <ScrollArea className="max-h-[400px]">
              {/* NEW ROWS */}
              {taskPreview.new_count > 0 && (
                <div className="mb-3">
                  <SectionHeader icon={Plus} title="New Rows (will be inserted)" count={taskPreview.new_count} color="text-emerald-600" section="new" />
                  {expandedSections.new && (
                    <div className="pl-6 space-y-1">
                      {(taskPreview.new_rows || []).map((r, i) => (
                        <div key={i} className="flex items-center gap-3 px-3 py-2 bg-emerald-50/50 rounded-lg text-sm">
                          <Badge className="bg-emerald-100 text-emerald-700 text-[9px]">NEW</Badge>
                          <span className="font-mono text-xs text-slate-500">{r.po_number}</span>
                          <span className="font-mono text-xs text-slate-500">{r.atlas_job_id}</span>
                          <span className="text-slate-700 truncate max-w-[200px]">{r.main_title}</span>
                          <Badge className="text-[9px] bg-blue-50 text-blue-600 border-0">{r.task}</Badge>
                          <span className="text-xs text-slate-500">{r.language}</span>
                          <span className="text-xs text-slate-500 ml-auto">{r.resource}</span>
                        </div>
                      ))}
                      {taskPreview.new_count > 50 && <p className="text-xs text-slate-400 px-3">...and {taskPreview.new_count - 50} more</p>}
                    </div>
                  )}
                </div>
              )}

              {/* CHANGED ROWS */}
              {taskPreview.changed_count > 0 && (
                <div className="mb-3">
                  <SectionHeader icon={Pencil} title="Changed Rows (will update original fields)" count={taskPreview.changed_count} color="text-amber-600" section="changed" />
                  {expandedSections.changed && (
                    <div className="pl-6 space-y-1">
                      {(taskPreview.changed_rows || []).map((r, i) => (
                        <div key={i} className="px-3 py-2 bg-amber-50/50 rounded-lg">
                          <div className="flex items-center gap-3 text-sm mb-1">
                            <Badge className="bg-amber-100 text-amber-700 text-[9px]">CHANGED</Badge>
                            <span className="font-mono text-xs text-slate-500">{r.po_number}</span>
                            <span className="font-mono text-xs text-slate-500">{r.atlas_job_id}</span>
                            <span className="text-slate-700 truncate max-w-[200px]">{r.main_title}</span>
                            <Badge className="text-[9px] bg-blue-50 text-blue-600 border-0">{r.task}</Badge>
                          </div>
                          <div className="flex flex-wrap gap-2 pl-2">
                            {(r.changes || []).map((c, ci) => (
                              <div key={ci} className="flex items-center gap-1 bg-white border border-amber-200 rounded px-2 py-0.5 text-[11px]">
                                <span className="font-medium text-amber-700">{c.field}:</span>
                                <span className="text-red-500 line-through">{String(c.old || "—")}</span>
                                <ArrowRight className="w-3 h-3 text-slate-400" />
                                <span className="text-emerald-600 font-medium">{String(c.new || "—")}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                      {taskPreview.changed_count > 50 && <p className="text-xs text-slate-400 px-3">...and {taskPreview.changed_count - 50} more</p>}
                    </div>
                  )}
                </div>
              )}

              {/* REMOVED ROWS */}
              {taskPreview.removed_count > 0 && (
                <div className="mb-3">
                  <SectionHeader icon={Minus} title="Removed from Original (will be marked)" count={taskPreview.removed_count} color="text-red-600" section="removed" />
                  {expandedSections.removed && (
                    <div className="pl-6 space-y-1">
                      {(taskPreview.removed_rows || []).map((r, i) => (
                        <div key={i} className="flex items-center gap-3 px-3 py-2 bg-red-50/50 rounded-lg text-sm">
                          <Badge className="bg-red-100 text-red-700 text-[9px]">REMOVED</Badge>
                          <span className="font-mono text-xs text-slate-500">{r.po_number}</span>
                          <span className="font-mono text-xs text-slate-500">{r.atlas_job_id}</span>
                          <span className="text-slate-700 truncate max-w-[200px]">{r.main_title}</span>
                          <span className="text-xs text-slate-500">was: {r.current_status}</span>
                        </div>
                      ))}
                      {taskPreview.removed_count > 50 && <p className="text-xs text-slate-400 px-3">...and {taskPreview.removed_count - 50} more</p>}
                    </div>
                  )}
                </div>
              )}

              {taskPreview.new_count === 0 && taskPreview.changed_count === 0 && taskPreview.removed_count === 0 && (
                <div className="text-center py-6 text-sm text-slate-400">
                  <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
                  No changes detected — database is up to date
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* ═══ DURATION MERGE PREVIEW ═══ */}
      {durationPreview && (
        <Card className="mb-6 border-violet-200" data-testid="duration-merge-preview">
          <CardHeader className="pb-3 bg-violet-50/50">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <GitMerge className="w-4 h-4 text-violet-600" />Duration Merge Preview
                <Badge className="bg-violet-100 text-violet-700 text-[10px]">{durationPreview.filename}</Badge>
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={() => setShowCommitConfirm(true)}
                  disabled={committingDuration}
                  className="bg-violet-600 hover:bg-violet-700"
                  data-testid="commit-duration-btn"
                >
                  {committingDuration ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
                  {committingDuration ? "Committing..." : "Commit Duration Merge"}
                </Button>
                <Button size="sm" variant="outline" onClick={cancelDurationPreview}>Cancel</Button>
              </div>
            </div>

            {/* Mode toggle */}
            <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="duration-mode-toggle">
              <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Commit Mode</span>
              <button
                type="button"
                onClick={() => setDurationMode("merge")}
                className={`px-3 py-1 rounded-full text-[11px] font-medium border transition-all ${
                  durationMode === "merge"
                    ? "bg-violet-600 text-white border-violet-600 shadow-sm"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                }`}
                data-testid="mode-merge"
              >
                Merge & overwrite changes
              </button>
              <button
                type="button"
                onClick={() => setDurationMode("add_only")}
                className={`px-3 py-1 rounded-full text-[11px] font-medium border transition-all ${
                  durationMode === "add_only"
                    ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                }`}
                data-testid="mode-add-only"
              >
                Add only (keep existing values)
              </button>
              {skippedChangeKeys.size > 0 && (
                <span className="ml-auto text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
                  {skippedChangeKeys.size} change{skippedChangeKeys.size === 1 ? "" : "s"} marked to skip
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="grid grid-cols-5 gap-3 mb-4">
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-slate-800">{durationPreview.uploaded_rows}</p>
                <p className="text-[10px] text-slate-500">Uploaded</p>
              </div>
              <div className="bg-emerald-50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-emerald-700">{durationPreview.matched_count}</p>
                <p className="text-[10px] text-emerald-600">Matched</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-amber-700">{durationPreview.changes_count}</p>
                <p className="text-[10px] text-amber-600">With Changes</p>
              </div>
              <div className="bg-sky-50 rounded-lg p-3 text-center" data-testid="stat-duration-only-new">
                <p className="text-lg font-bold text-sky-700">{durationPreview.duration_only_new_count || 0}</p>
                <p className="text-[10px] text-sky-600">
                  New ({(durationPreview.duration_only_tasks || []).join(", ") || "duration-only"})
                </p>
              </div>
              <div className="bg-red-50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-red-700">{durationPreview.not_found_count}</p>
                <p className="text-[10px] text-red-600">Not Found</p>
              </div>
            </div>

            {/* Duration-only new tasks — will be created as fresh adjustments */}
            {(durationPreview.duration_only_new_count || 0) > 0 && (
              <div className="mb-3 bg-sky-50/50 border border-sky-200 rounded-lg px-3 py-2" data-testid="duration-only-new-panel">
                <p className="text-xs font-semibold text-sky-800 flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {durationPreview.duration_only_new_count} new {(durationPreview.duration_only_tasks || []).join(", ")} tasks will be created
                  <span className="ml-2 text-[10px] font-normal text-slate-500">— resource is left blank so you can assign in Database Center</span>
                </p>
                {(durationPreview.duration_only_new_sample || []).length > 0 && (
                  <div className="mt-2 grid grid-cols-2 gap-1 text-[11px] text-slate-600 max-h-32 overflow-y-auto">
                    {durationPreview.duration_only_new_sample.slice(0, 12).map((r, i) => (
                      <div key={i} className="flex items-center gap-1.5">
                        <span className="text-slate-400">•</span>
                        <span className="font-medium">{r.language || "—"}</span>
                        <span className="truncate">{r.main_title}</span>
                        <span className="text-slate-400">·</span>
                        <span className="font-mono text-[10px]">{r.po_number}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* PO Status Changes */}
            {(durationPreview.po_status_changes || []).length > 0 && (
              <div className="mb-3">
                <p className="text-xs font-semibold text-violet-700 mb-2 flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" />PO Status Changes ({durationPreview.po_status_changes.length})
                  <span className="ml-2 text-[10px] font-normal text-slate-400">— uncheck to keep current value</span>
                </p>
                <div className="space-y-1 max-h-[200px] overflow-y-auto">
                  {durationPreview.po_status_changes.map((c, i) => {
                    const key = `${c.id}:po_status`;
                    const skipped = skippedChangeKeys.has(key);
                    const disabled = durationMode === "add_only";
                    return (
                      <div key={i} className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm ${skipped || disabled ? "bg-slate-50 opacity-60" : "bg-violet-50/50"}`}>
                        <input
                          type="checkbox"
                          checked={!skipped && !disabled}
                          disabled={disabled}
                          onChange={() => toggleSkipChange(c.id, "po_status")}
                          className="h-4 w-4 accent-violet-600 cursor-pointer"
                          data-testid={`skip-${c.id}-po_status`}
                        />
                        <span className="font-mono text-xs text-slate-500">{c.po_number}</span>
                        <span className="text-slate-700 truncate max-w-[200px]">{c.main_title}</span>
                        <div className="flex items-center gap-1 ml-auto">
                          <Badge className="text-[9px] bg-slate-100 text-slate-600">{c.old_status || "—"}</Badge>
                          <ArrowRight className="w-3 h-3 text-slate-400" />
                          <Badge className={`text-[9px] ${
                            (skipped || disabled) ? "bg-slate-100 text-slate-400 line-through" :
                            c.new_status?.toLowerCase() === "approved" ? "bg-emerald-100 text-emerald-700" :
                            c.new_status?.toLowerCase() === "completed" ? "bg-blue-100 text-blue-700" :
                            "bg-amber-100 text-amber-700"
                          }`}>{c.new_status}</Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Duration Changes */}
            {(durationPreview.duration_changes || []).length > 0 && (
              <div>
                <p className="text-xs font-semibold text-amber-700 mb-2 flex items-center gap-1">
                  Duration Changes ({durationPreview.duration_changes.length})
                  <span className="ml-2 text-[10px] font-normal text-slate-400">— uncheck to keep current value</span>
                </p>
                <div className="space-y-1 max-h-[150px] overflow-y-auto">
                  {durationPreview.duration_changes.map((c, i) => {
                    const key = `${c.id}:duration_words`;
                    const skipped = skippedChangeKeys.has(key);
                    const disabled = durationMode === "add_only";
                    return (
                      <div key={i} className={`flex items-center gap-3 px-3 py-1.5 rounded text-sm ${skipped || disabled ? "bg-slate-50 opacity-60" : "bg-amber-50/50"}`}>
                        <input
                          type="checkbox"
                          checked={!skipped && !disabled}
                          disabled={disabled}
                          onChange={() => toggleSkipChange(c.id, "duration_words")}
                          className="h-4 w-4 accent-amber-600 cursor-pointer"
                          data-testid={`skip-${c.id}-duration_words`}
                        />
                        <span className="font-mono text-xs text-slate-500">{c.po_number}</span>
                        <span className={`text-xs ${skipped || disabled ? "text-slate-400" : "text-red-500 line-through"}`}>{c.old}</span>
                        <ArrowRight className="w-3 h-3 text-slate-400" />
                        <span className={`font-medium text-xs ${skipped || disabled ? "text-slate-400 line-through" : "text-emerald-600"}`}>{c.new}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Commit Confirmation Dialog */}
      <Dialog open={showCommitConfirm} onOpenChange={setShowCommitConfirm}>
        <DialogContent className="max-w-md" data-testid="commit-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitMerge className="w-5 h-5 text-violet-600" />
              Confirm Duration Commit
            </DialogTitle>
            <DialogDescription>
              {durationMode === "merge"
                ? "Existing values will be OVERWRITTEN with the new uploaded values."
                : "Existing values will be KEPT. Rows still get verified."}
            </DialogDescription>
          </DialogHeader>

          {durationPreview && (
            <div className="space-y-3 my-2">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-slate-50 rounded p-2">
                  <p className="text-[10px] uppercase text-slate-400">Mode</p>
                  <p className={`font-semibold ${durationMode === "merge" ? "text-violet-700" : "text-emerald-700"}`}>
                    {durationMode === "merge" ? "Merge & overwrite" : "Add only"}
                  </p>
                </div>
                <div className="bg-slate-50 rounded p-2">
                  <p className="text-[10px] uppercase text-slate-400">Rows verified</p>
                  <p className="font-semibold text-slate-700">{durationPreview.matched_count}</p>
                </div>
                <div className="bg-slate-50 rounded p-2">
                  <p className="text-[10px] uppercase text-slate-400">Changes detected</p>
                  <p className="font-semibold text-slate-700">{durationPreview.changes_count}</p>
                </div>
                <div className="bg-slate-50 rounded p-2">
                  <p className="text-[10px] uppercase text-slate-400">Marked to skip</p>
                  <p className="font-semibold text-amber-700">{skippedChangeKeys.size}</p>
                </div>
              </div>

              <div className="text-[11px] text-slate-500 bg-amber-50 border border-amber-100 rounded p-2 leading-relaxed">
                {durationMode === "merge" ? (
                  <>
                    <strong className="text-amber-700">Heads-up:</strong> {durationPreview.changes_count - skippedChangeKeys.size} change{(durationPreview.changes_count - skippedChangeKeys.size) === 1 ? "" : "s"} will be applied. Skipped changes will leave the existing value untouched.
                  </>
                ) : (
                  <>
                    <strong className="text-emerald-700">Add only mode:</strong> No existing values will be changed. All {durationPreview.matched_count} matched rows will simply be marked as verified.
                  </>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowCommitConfirm(false)} data-testid="commit-cancel-btn">
              Back
            </Button>
            <Button
              size="sm"
              onClick={commitDurationMerge}
              disabled={committingDuration}
              className={durationMode === "merge" ? "bg-violet-600 hover:bg-violet-700" : "bg-emerald-600 hover:bg-emerald-700"}
              data-testid="commit-confirm-btn"
            >
              {committingDuration ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
              {committingDuration ? "Committing…" : (durationMode === "merge" ? "Apply Merge" : "Apply Add-Only")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Info Card */}
      <Card className="border-slate-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
              <GitMerge className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-800 mb-1">How Incremental Merge Works</h3>
              <div className="text-xs text-slate-500 space-y-1">
                <p><strong>Composite Key:</strong> PO Number + Atlas Job ID + Task Type + Language</p>
                <p><strong>Original fields</strong> (updated from upload): Status, Duration, Title, Episode, Due Date</p>
                <p><strong>Adjustment fields</strong> (never overwritten): Assigned Resource, Rate, Amount, Billing</p>
                <p><strong>Removed rows:</strong> Marked as "Removed from Original" — never deleted (kept for invoicing)</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Legacy Adj Copy Dialog */}
      <Dialog open={showAdjCopyPrompt} onOpenChange={setShowAdjCopyPrompt}>
        <DialogContent data-testid="adj-copy-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Database className="w-5 h-5 text-blue-600" />Create Adjustment Database?</DialogTitle>
            <DialogDescription>Sync completed. Copy new records into the Adjustment Database (working copy used across the app)?</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-3 pt-4">
            <Button onClick={handleCreateAdjCopy} disabled={adjCopying} className="flex-1" data-testid="confirm-adj-copy-btn">{adjCopying ? "Creating..." : "Yes, Create Copy"}</Button>
            <Button variant="outline" onClick={() => setShowAdjCopyPrompt(false)} className="flex-1">Skip</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Changelog Dialog */}
      <Dialog open={showChangelog} onOpenChange={setShowChangelog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="changelog-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="w-5 h-5 text-indigo-600" />Merge Change Log</DialogTitle>
            <DialogDescription>History of all database merge operations</DialogDescription>
          </DialogHeader>
          {changelogDetail ? (
            <div>
              <Button variant="ghost" size="sm" onClick={() => setChangelogDetail(null)} className="mb-3">
                <ChevronRight className="w-3.5 h-3.5 mr-1 rotate-180" />Back to list
              </Button>
              <div className="space-y-2 mb-4">
                <p className="text-sm"><strong>Type:</strong> {changelogDetail.merge_type}</p>
                <p className="text-sm"><strong>File:</strong> {changelogDetail.filename}</p>
                <p className="text-sm"><strong>Date:</strong> {new Date(changelogDetail.committed_at).toLocaleString()}</p>
                <div className="grid grid-cols-4 gap-2">
                  <div className="bg-emerald-50 rounded p-2 text-center"><p className="text-sm font-bold text-emerald-700">{changelogDetail.inserted || 0}</p><p className="text-[10px] text-emerald-600">Inserted</p></div>
                  <div className="bg-amber-50 rounded p-2 text-center"><p className="text-sm font-bold text-amber-700">{changelogDetail.updated || 0}</p><p className="text-[10px] text-amber-600">Updated</p></div>
                  <div className="bg-slate-50 rounded p-2 text-center"><p className="text-sm font-bold text-slate-600">{changelogDetail.skipped || 0}</p><p className="text-[10px] text-slate-500">Skipped</p></div>
                  <div className="bg-red-50 rounded p-2 text-center"><p className="text-sm font-bold text-red-700">{changelogDetail.marked_removed || 0}</p><p className="text-[10px] text-red-600">Removed</p></div>
                </div>
              </div>
              <ScrollArea className="max-h-[400px]">
                <div className="space-y-1.5">
                  {(changelogDetail.entries || []).slice(0, 100).map((e, i) => (
                    <div key={i} className="px-3 py-2 bg-slate-50 rounded text-xs">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={`text-[8px] ${e.action === "insert" ? "bg-emerald-100 text-emerald-700" : e.action === "update" || e.action === "duration_update" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                          {e.action.toUpperCase()}
                        </Badge>
                        <span className="font-mono text-slate-500">{e.key}</span>
                      </div>
                      {e.changes && (
                        <div className="flex flex-wrap gap-1 pl-2">
                          {e.changes.map((c, ci) => (
                            <span key={ci} className="inline-flex items-center gap-0.5 bg-white border rounded px-1.5 py-0.5">
                              <span className="font-medium text-slate-700">{c.field}:</span>
                              <span className="text-red-500">{String(c.old || "—")}</span>
                              <ArrowRight className="w-2.5 h-2.5 text-slate-400 inline" />
                              <span className="text-emerald-600">{String(c.new || "—")}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          ) : (
            <div className="space-y-2">
              {changelog.length === 0 ? (
                <p className="text-center py-8 text-sm text-slate-400">No merge history yet</p>
              ) : changelog.map((log) => (
                <div key={log.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => loadChangelogDetail(log.id)}>
                  <GitMerge className={`w-4 h-4 ${log.merge_type === "task" ? "text-blue-600" : "text-violet-600"}`} />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-800">{log.filename || log.merge_type}</p>
                    <p className="text-xs text-slate-500">
                      {new Date(log.committed_at).toLocaleString()} —
                      {log.inserted > 0 && ` ${log.inserted} added`}
                      {log.updated > 0 && ` ${log.updated} updated`}
                      {log.marked_removed > 0 && ` ${log.marked_removed} removed`}
                    </p>
                  </div>
                  <Eye className="w-4 h-4 text-slate-400" />
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DatabaseSync;
